import type { MessageKey } from './sk';

/** English. Partial on purpose: a key missing here falls back to Slovak, never to the key itself. */
export const EN: Partial<Record<MessageKey, string>> = {
  'app.name': 'Stock management',
  'app.title': 'Stock management — back office',
  'app.badge': 'back office',
  'common.retry': 'Try again',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.close': 'Close',
  'common.edit': 'Edit',
  'common.archive': 'Archive',
  'common.restore': 'Restore',
  'common.actions': 'Actions',
  'common.search': 'Search',
  'common.all': 'All',
  'common.none': '—',
  'common.unlimited': 'unlimited',
  'layout.openMenu': 'Open menu',
  'layout.signOut': 'Sign out',
  'theme.toLight': 'Switch to light mode',
  'theme.toDark': 'Switch to dark mode',
  'theme.toggle': 'Toggle colour mode',
  'language.toggle': 'Language',
  'error.network.title': 'The server is not answering',
  'error.network.detail':
    'The back office points at {url}. Check that the API is running there and try again.',
  'error.request.title': 'Error {status}',
  'error.unexpected.title': 'Unexpected error',
  'error.unexpected.detail': 'Something broke in the back office. The details are in the browser console.',
  'error.requestFailed': 'The request failed.',
  'pagination.range': '{from}–{to} of {count}',
  'pagination.next': 'Next page',
  'pagination.previous': 'Previous page',
  'pagination.first': 'First page',
  'pagination.last': 'Last page',
  'pagination.rowsPerPage': 'Rows per page',
  'nav.overview': 'Overview',
  'nav.stock': 'Stock',
  'nav.products': 'Products',
  'nav.movements': 'Movements',
  'nav.administration': 'Administration',
  'nav.companies': 'Companies',
  'nav.users': 'Users',
  'nav.platform': 'Platform',
  'nav.tenants': 'Businesses',
  'nav.audit': 'Audit',
  'role.superAdmin': 'Super admin',
  'role.owner': 'Owner',
  'role.subUser': 'Sub-user',
  'status.ACTIVE': 'Active',
  'status.ARCHIVED': 'Archived',
  'status.DISABLED': 'Disabled',
  'licenceState.ACTIVE': 'Active',
  'licenceState.SUSPENDED': 'Suspended',
  'licenceState.EXPIRED': 'Expired',
  'licenceState.NOT_YET_VALID': 'Not yet valid',
  'actorKind.PLATFORM': 'platform',
  'actorKind.TENANT': 'business',
  'actorKind.SYSTEM': 'system',
  'movementType.RECEIPT': 'Receipt',
  'movementType.ISSUE': 'Issue',
  'movementType.ADJUSTMENT': 'Adjustment',
  'movementType.WRITE_OFF': 'Write-off',
  'movementReason.OPENING_FROM_SHOP': 'Opening stock from the e-shop',
  'movementSource.BACK_OFFICE': 'back office',
  'movementSource.INTEGRATION': 'integration',
  'movementSource.SYSTEM': 'system',
  'field.email': 'E-mail',
  'field.password': 'Password',
  'field.displayName': 'Name',
  'field.tenantName': 'Business name',
  'field.contactEmail': 'Contact e-mail',
  'field.licenceState': 'Licence state',
  'field.createdAt': 'Created',
  'field.lastLoginAt': 'Last sign-in',
  'field.status': 'Status',
  'field.company': 'Company',
  'field.companyName': 'Company name',
  'field.registrationNumber': 'Registration no.',
  'field.vatNumber': 'VAT no.',
  'field.currency': 'Currency',
  'field.productName': 'Product',
  'field.ean': 'EAN',
  'field.sku': 'SKU',
  'field.defaultPrice': 'Default price ({currency})',
  'field.price': 'Batch price ({currency})',
  'field.purchasePrice': 'Purchase price ({currency})',
  'field.expiryDate': 'Expiry date',
  'field.lotNumber': 'Lot number',
  'field.batch': 'Batch',
  'field.quantity': 'Quantity (pcs)',
  'field.reference': 'Reference',
  'field.note': 'Note',
  'field.movementType': 'Movement type',
  'password.generate': 'Generate',
  'password.hint': 'Shown only now — pass it on before you close the dialog. It is stored only as a hash.',
  'password.show': 'Show password',
  'password.hide': 'Hide password',
  'password.reset': 'Set a new password',
  'password.resetTitle': 'New password — {subject}',
  'password.resetDone': 'The password is changed. Sessions ended: {count}.',
  'login.kind.business': 'Business',
  'login.kind.platform': 'Platform',
  'login.hint.business': 'Sign-in for the owners and users of a business.',
  'login.hint.platform': 'Sign-in for platform super admins. A business account cannot sign in here.',
  'login.submit': 'Sign in',
  'login.busy': 'Signing in…',
  'login.unreachable':
    'The server is not answering. Check that the API is running and try again. This is not a problem with your credentials.',
  'login.failed': 'Sign-in failed. Please try again.',
  'nav.invoices': 'Invoices',
  'nav.settings': 'Settings',
  'nav.billing': 'Billing',
  'licenceState.AWAITING_PAYMENT': 'Awaiting payment',
  'field.legalName': 'Registered name',
  'field.street': 'Street and number',
  'field.city': 'City',
  'field.postalCode': 'Postal code',
  'field.country': 'Country (code)',
  'field.taxNumber': 'Tax number (DIČ)',
  'field.vatPayer': 'VAT payer',
  'field.vatRate': 'VAT rate (%)',
  'field.registerEntry': 'Register entry',
  'field.registerEntryHint':
    'E.g. Zapísaná v OR Mestského súdu Bratislava III, oddiel Sro, vložka č. 12345/B',
  'field.billingEmail': 'Billing e-mail',
  'field.phone': 'Phone',
  'field.web': 'Web',
  'field.bankName': 'Bank',
  'field.iban': 'IBAN',
  'field.swift': 'SWIFT',
  'field.proformaPrefix': 'Proforma number prefix',
  'field.invoicePrefix': 'Invoice number prefix',
  'field.dueDays': 'Due in (days)',
  'field.issuedBy': 'Issued by',
  'field.months': 'Months',
  'field.documentType': 'Document',
  'field.number': 'Number',
  'field.buyer': 'Buyer',
  'field.issuedOn': 'Issued',
  'field.dueOn': 'Due',
  'field.total': 'Total',
  'field.paid': 'Paid',
  'field.amount': 'Amount (€)',
  'field.receivedOn': 'Received on',
  'field.paymentReference': 'Payment reference',
  'field.variableSymbol': 'Variable symbol',
  'documentType.PROFORMA': 'Proforma invoice',
  'documentType.INVOICE': 'Invoice',
  'invoiceStatus.ISSUED': 'Unpaid',
  'invoiceStatus.PARTIALLY_PAID': 'Partly paid',
  'invoiceStatus.PAID': 'Paid',
  'invoiceStatus.CANCELLED': 'Cancelled',
  'invoice.overdue': 'Overdue',
  'settings.title': 'Billing settings',
  'settings.subtitle': 'The supplier printed on every document, and the licence price list.',
  'settings.missing':
    'Until both the supplier and the price list are filled in, no business can be created and no licence ordered.',
  'settings.supplier': 'Supplier',
  'settings.supplierHint':
    'These details are printed on every new document. Documents already issued do not change.',
  'settings.numbering': 'Numbering and due date',
  'settings.bank': 'Bank account',
  'settings.saved': 'Saved.',
  'settings.invalid':
    'Check the number fields — they must be whole numbers of zero or more, the VAT rate with at most two decimals.',
  'settings.priceList': 'Price list',
  'settings.priceListHint':
    'Monthly prices without VAT. A licence is invoiced as the base plus each limit, for every month. An unlimited limit has its own price.',
  'price.base': 'Licence base / month (€)',
  'price.unit': 'Price per block / month (€)',
  'price.unitSize': 'Block size',
  'price.unlimited': 'Unlimited / month (€)',
  'invoices.title': 'Invoices',
  'invoices.subtitle': 'Proformas and invoices for licences, of every business.',
  'invoices.searchHint': 'Number, buyer or variable symbol',
  'invoices.overdueOnly': 'Overdue only',
  'invoices.tenantFilter': 'Business: {name}',
  'invoices.clearTenant': 'All businesses',
  'invoices.empty': 'No documents',
  'invoices.emptyDetail': 'Documents are issued when a business is created and when a licence is ordered.',
  'invoice.back': 'Back to invoices',
  'invoice.download': 'Download PDF',
  'invoice.recordPayment': 'Record a payment',
  'invoice.cancel': 'Cancel the document',
  'invoice.cancelConfirm':
    'Cancel proforma {number}? The licence order is cancelled and the buyer is told not to pay it.',
  'invoice.supplier': 'Supplier',
  'invoice.buyer': 'Buyer',
  'invoice.lines': 'Lines',
  'invoice.line': 'Description',
  'invoice.quantity': 'Quantity',
  'invoice.unitPrice': 'Unit price',
  'invoice.lineTotal': 'Amount',
  'invoice.net': 'Net',
  'invoice.vat': 'VAT {rate} %',
  'invoice.noVat': 'The supplier is not a VAT payer.',
  'invoice.total': 'Total',
  'invoice.remaining': 'Remaining',
  'invoice.overpaid': 'Overpaid',
  'invoice.payments': 'Payments',
  'invoice.noPayments': 'No payment yet.',
  'invoice.deletePayment': 'Remove',
  'invoice.settles': 'Settles proforma {number}',
  'invoice.openSettled': 'Open the settling invoice',
  'invoice.openProforma': 'Open the proforma',
  'invoice.order': 'Licence order',
  'invoice.orderKind.NEW': 'New licence',
  'invoice.orderKind.RENEWAL': 'Renewal',
  'invoice.orderSummary': '{kind}, {months} months',
  'invoice.tenant': 'Business',
  'payment.title': 'Payment for {number}',
  'payment.hint':
    'When the payments reach the total, the licence is activated, the invoice is issued and mailed to the buyer.',
  'payment.invalidAmount': 'Enter an amount above zero, with at most two decimals.',
  'tenants.billingCompany': 'Billing company',
  'tenants.billingCompanyHint':
    'The company the proforma is addressed to. It becomes the business’s first company; the owner can change it later.',
  'tenants.noBillingCompany': 'Not set',
  'tenants.notBillable': 'address or IČO missing',
  'tenants.invoices': 'The business’s invoices',
  'tenants.firstOrder': 'First licence',
  'licence.order': 'Order a licence',
  'licence.orderTitle': 'Licence order — {tenant}',
  'licence.orderHint':
    'A proforma is issued. The new period and limits take effect when it is paid; until then the current licence applies.',
  'licence.awaitingHint': 'The licence awaits payment of its proforma. Recording the payment activates it.',
  'licence.orderInvalid': 'Enter 1 to 60 months and whole-number limits.',
  'quote.title': 'Price from the price list',
  'quote.unavailable':
    'The price cannot be computed — fill in the supplier and the price list in the settings.',
  'billing.title': 'Billing',
  'billing.subtitle': 'Licence documents, and the company they are addressed to.',
  'billing.company': 'Invoice to company',
  'billing.companyHint':
    'Future documents are addressed to the chosen company. It must be active and have an address and an IČO — add them under Companies.',
  'billing.saved': 'The billing company is changed.',
  'billing.invoices': 'Documents',
  'billing.empty': 'No documents yet',
  'billing.emptyDetail': 'Licence documents appear here as soon as they are issued.',
  'billing.awaiting':
    'The licence awaits payment of proforma {number} for {total}, due {due}. It is activated as soon as the payment arrives.',
  'overview.awaitingPayment':
    'The licence awaits payment of its proforma. You can view your data; changes will be possible once it is paid.',
  'overview.toBilling': 'View the documents',
  'companies.billingSection': 'Billing details',
  'companies.billingHint': 'An address and an IČO are needed for invoices to be addressed to the company.',
  'login.forgot': 'Forgot your password?',
  'forgot.title': 'Reset your password',
  'forgot.hint': 'Enter the e-mail you sign in with. We will send you a link to set a new password.',
  'forgot.submit': 'Send the link',
  'forgot.busy': 'Sending…',
  'forgot.sent':
    'If {email} has an account, we have sent it a link to set a new password. The link works for one hour.',
  'forgot.back': 'Back to sign-in',
  'setPassword.title.reset': 'New password',
  'setPassword.title.invitation': 'Welcome — set your password',
  'setPassword.hint': 'Choose a password you do not use anywhere else. The link works once.',
  'setPassword.password': 'New password',
  'setPassword.confirm': 'New password again',
  'setPassword.mismatch': 'The passwords do not match.',
  'setPassword.submit': 'Set the password',
  'setPassword.busy': 'Saving…',
  'setPassword.done': 'The password is set and every earlier session has ended. You can sign in now.',
  'setPassword.toLogin': 'Sign in',
  'setPassword.missingToken':
    'The link is incomplete. Open it straight from the e-mail, or ask for a new one.',
  'setPassword.requestNew': 'Ask for a new link',
  'invitation.pending': 'Invited',
  'invitation.resend': 'Resend the invitation',
  'invitation.sent': 'Invitation sent',
  'invitation.hint':
    'An invitation with a link arrives by e-mail; the user sets their own password with it. The link works for 7 days.',
  'licence.validFrom': 'Valid from',
  'licence.validUntil': 'Valid until',
  'licence.validUntilHint': 'Empty = no end',
  'licence.noEnd': 'no end',
  'licence.limitHint': 'Empty = unlimited',
  'licence.notes': 'Licence notes',
  'licence.metric.companies': 'Companies',
  'licence.metric.subUsers': 'Sub-users',
  'licence.metric.products': 'Products',
  'licence.metric.totalStock': 'Total stock (pcs)',
  'licence.invalid':
    'Check the licence: limits must be whole non-negative numbers and the end may not precede the start.',
  'licence.save': 'Save licence',
  'licence.saved': 'The licence is saved.',
  'licence.lowerHint':
    'Lowering a limit below current usage deletes nothing — the business simply cannot add more.',
  'licence.suspend': 'Suspend licence',
  'licence.suspendReason': 'Reason',
  'licence.suspendHint': 'The business becomes read-only at once. The reason is written to the audit log.',
  'licence.reactivate': 'Reactivate licence',
  'tenants.title': 'Businesses',
  'tenants.subtitle': 'Every business on the platform, its owner and its licence.',
  'tenants.create': 'New business',
  'tenants.edit': 'Edit business',
  'tenants.owner': 'Owner',
  'tenants.licence': 'Licence',
  'tenants.usage': 'Licence usage',
  'tenants.searchHint': 'name or e-mail',
  'tenants.empty': 'No business',
  'tenants.emptyDetail': 'Create the first business together with its owner and licence.',
  'tenants.back': 'Back to businesses',
  'audit.title': 'Audit',
  'audit.subtitle': 'Every super-admin action and every change to a business user.',
  'audit.when': 'When',
  'audit.tenant': 'Business',
  'audit.actor': 'Who',
  'audit.action': 'Action',
  'audit.target': 'Target',
  'audit.note': 'Note',
  'audit.empty': 'No entries',
  'audit.emptyDetail': 'Try a different action filter.',
  'overview.title': 'Overview',
  'overview.greeting': 'Signed in as {name}.',
  'overview.readOnly':
    'The business licence is not valid — you can read but not change anything. Contact the platform administrator.',
  'overview.licence': 'Licence',
  'overview.validity': 'Valid from {from} until {until}.',
  'overview.validityOpen': 'Valid from {from}, with no end.',
  'overview.welcome': 'Welcome. The menu on the left lists the screens you can use.',
  'companies.title': 'Companies',
  'companies.subtitle':
    'The legal entities of the business. Products and stock always belong to one company.',
  'companies.create': 'New company',
  'companies.edit': 'Edit company',
  'companies.currencyHint': 'A three-letter code such as EUR. It cannot be changed later.',
  'companies.empty': 'No company',
  'companies.emptyManage': 'Create the first company — products come after.',
  'companies.emptyAsk': 'You cannot reach any company. Ask the owner of the business.',
  'users.title': 'Users',
  'users.subtitle':
    'Who may do what, company by company. You can grant only the permissions you hold yourself.',
  'users.create': 'New user',
  'users.rights': 'Permissions',
  'users.grantCount': '{count} permissions',
  'users.permissions': 'Permissions',
  'users.permissionsTitle': 'Permissions — {user}',
  'users.hiddenGrants':
    'The user also holds {count} permissions in archived companies. They are not shown here and are kept when you save.',
  'users.deactivate': 'Deactivate',
  'users.reactivate': 'Reactivate',
  'users.empty': 'No user',
  'users.emptyDetail': 'Try a different filter.',
  'permissions.tenantWide': 'Whole business',
  'permissions.perCompany': 'Per company',
  'permissions.noCompanies': 'The business has no active company yet.',
  'products.title': 'Products',
  'products.subtitle': "The selected company's catalogue. Each product's stock is held in batches.",
  'products.create': 'New product',
  'products.edit': 'Edit product',
  'products.searchHint': 'name, EAN or SKU',
  'products.defaultPrice': 'Default price',
  'products.batches': 'Batches',
  'products.onHand': 'On hand',
  'products.eanHint': '8, 12, 13 or 14 digits with a valid check digit. Optional.',
  'products.priceHint': 'Used by batches without a price of their own. Empty = no price.',
  'products.empty': 'No product',
  'products.emptyManage': 'Create the first product of this company.',
  'products.emptyRead': 'This company has no products yet, or the filter found nothing.',
  'products.noCompany': 'No company available',
  'products.noCompanyDetail': 'Products belong to a company. You cannot reach the products of any company.',
  'products.back': 'Back to products',
  'batches.title': 'Batches',
  'batches.create': 'New batch',
  'batches.edit': 'Batch prices',
  'batches.prices': 'Prices',
  'batches.effectivePrice': 'Price',
  'batches.purchasePrice': 'Purchase price',
  'batches.inherited': 'from the product',
  'batches.noExpiry': 'no expiry',
  'batches.onlyInStock': 'In stock only',
  'batches.priceHint': "Empty = the product's default price.",
  'batches.empty': 'No batch',
  'batches.emptyReceive': 'Stock comes from a receipt. A receipt with a new expiry or lot creates the batch.',
  'batches.emptyRead': 'The product has no stock yet, or the filter found nothing.',
  'batch.expires': 'expires {date}',
  'batch.lot': 'lot {lot}',
  'batch.plain': 'no batch',
  'stock.receipt': 'Receive',
  'stock.issue': 'Issue',
  'stock.adjustment': 'Count',
  'stock.writeOff': 'Write off',
  'stock.newBatch': 'New batch…',
  'stock.fefo': 'Automatic — earliest expiry first',
  'stock.fefoHint':
    'The batch with the earliest expiry goes first, then the next; batches without a date go last. Expired batches are ordinary stock.',
  'stock.onHandShort': '{count} pcs',
  'stock.counted': 'Counted quantity (pcs)',
  'stock.countedHint': 'Recorded now: {count} pcs. The difference is written as a movement.',
  'stock.piecesHint': 'Whole pieces.',
  'stock.referenceHint': 'A document, order or delivery-note number.',
  'stock.quantityInvalid': 'The quantity must be a whole positive number.',
  'stock.batchRequired': 'Choose a batch.',
  'stock.done': 'Movements recorded: {count}.',
  'stock.replayed': 'This operation was already recorded — nothing changed.',
  'money.invalid': 'Enter a price as a number with at most two decimals, for example 12.50.',
  'movements.title': 'Movements',
  'movements.subtitle': 'Every stock change of the selected company, newest first.',
  'movements.when': 'When',
  'movements.delta': 'Change',
  'movements.after': 'After',
  'movements.who': 'Who',
  'movements.oneProduct': 'One product',
  'movements.empty': 'No movements',
  'movements.emptyDetail': "Movements come from receipts, issues, counts and write-offs on a product's page.",
  'movements.noCompanyDetail': 'You cannot reach the movements of any company.',
  'nav.integrations': 'Linked e-shops',
  'connectors.title': 'Connectors to download',
  'connectors.subtitle':
    "Install the connector in the shop (PrestaShop: Modules → Upload a module) and paste the link's key into it. Offered is the version released with this app, for each supported line of platform versions.",
  'connectors.version': 'Connector version',
  'connectors.download': 'Download',
  'integrations.title': 'Linked e-shops',
  'integrations.subtitle':
    'E-shops linked to the selected company. The stock here is master: a shop reports orders and returns, and receives the levels.',
  'integrations.create': 'Link an e-shop',
  'integrations.shop': 'E-shop',
  'integrations.lastSeen': 'Last heard from',
  'integrations.mapped': 'Mapped',
  'integrations.unmatched': 'To map',
  'integrations.openConflicts': 'Conflicts',
  'integrations.pending': 'Waiting to send',
  'integrations.notConnected': 'Not connected yet',
  'integrations.empty': 'No linked e-shop',
  'integrations.emptyDetail':
    'Linking issues a one-time key you paste into the connector in your shop (for PrestaShop, the stocksync module). The licence sets how many shops of each platform you may link.',
  'integrations.noCompany': 'No company',
  'integrations.noCompanyDetail': 'You cannot see the linked e-shops of any company.',
  'linkStatus.PENDING': 'Waiting to connect',
  'linkStatus.ACTIVE': 'Connected',
  'linkStatus.DISABLED': 'Paused',
  'linkStatus.REVOKED': 'Revoked',
  'scope.stock.read': 'Read stock levels',
  'scope.stock.write': 'Report orders and returns',
  'linkSettings.createTitle': 'Link an e-shop',
  'linkSettings.editTitle': 'Link settings',
  'linkSettings.create': 'Create and show the key',
  'linkSettings.name': 'Name',
  'linkSettings.nameHint': 'The shop appears under this name in movements and conflicts.',
  'linkSettings.scopes': 'What the shop may do',
  'linkSettings.allowlist': 'Allowed IP addresses',
  'linkSettings.allowlistHint':
    'Addresses of the shop server, separated by commas or new lines (IPv4, ranges such as /24). Empty = any address.',
  'linkSettings.incomplete': 'Fill in the name and choose at least one permission.',
  'connectionToken.title': 'Connection key: {name}',
  'connectionToken.once':
    'The key is shown only now. Only its fingerprint is stored — once closed nobody can show it again; a new one can be issued.',
  'connectionToken.token': 'One-time key',
  'connectionToken.copy': 'Copy the key',
  'connectionToken.copied': 'Copied to the clipboard.',
  'connectionToken.validUntil': 'Valid until {when}, and works once.',
  'connectionToken.stepsTitle': 'Connecting the shop',
  'connectionToken.step2': 'Paste this key into the connector settings and click Connect.',
  'connectionToken.step3':
    'The connector sends its catalogue for matching — nothing else. Until you choose the first sync in the shop detail, no quantity changes and the shop’s orders wait.',
  'connectionToken.done': 'I have saved it',
  'detail.newToken': 'New key',
  'detail.repair': 'Pair again',
  'detail.fullSync': 'Full sync',
  'detail.pause': 'Pause',
  'detail.resume': 'Resume',
  'detail.revoke': 'Revoke link',
  'detail.revokeTitle': 'Revoke {name}?',
  'detail.revokeMessage':
    'The shop loses access at once and its secret is destroyed. A revoked link cannot be restored — it frees its place in the licence for a new one.',
  'detail.pendingHint': 'The shop is not connected yet. The key is valid until {when}.',
  'detail.tokenExpired': 'The shop is not connected and its key has expired. Issue a new one.',
  'detail.fullSyncPending': 'A full sync was requested ({when}). The shop runs it on its next contact.',
  'detail.versions': 'Versions',
  'detail.connected': 'Connected',
  'detail.lastDelivered': 'Last took levels',
  'detail.lastFullSync': 'Last full sync',
  'detail.anyAddress': 'Any address',
  'detail.tabMappings': 'Items ({count} to map)',
  'detail.tabConflicts': 'Conflicts ({count})',
  'detail.tabLog': 'Communication log',
  'detail.tabListings': 'Products in the e-shop',
  'listings.notSupported':
    'The e-shop’s module does not take products from the app — it gets stock levels only. Install its newer version.',
  'listings.firstSyncPending': 'Products are sent to the e-shop after the first sync.',
  'listings.publish': 'Publish products',
  'listings.resend': 'Send all again',
  'listings.importAll': 'Take all from the e-shop',
  'listings.publishSelected': 'Publish selected',
  'listings.unpublishSelected': 'Hide selected',
  'listings.importSelected': 'Take selected from the e-shop',
  'listings.selected': 'Selected: {count}',
  'listings.product': 'Product',
  'listings.visibility': 'In the e-shop',
  'listings.published': 'Published',
  'listings.hidden': 'Hidden',
  'listings.shopId': 'E-shop ID',
  'listings.lastSent': 'Last sent',
  'listings.skipped': 'The e-shop skipped: {list}',
  'listings.importPending': 'Waiting to be taken from the e-shop',
  'listings.imported': 'Taken from the e-shop {when}',
  'listings.importError': 'Taking: {error}',
  'listings.republish': 'Publish',
  'listings.unpublish': 'Hide',
  'listings.take': 'Take',
  'listings.empty': 'No products',
  'listings.emptyDetail':
    'The products the e-shop sells show here after the first sync, or when you publish them.',
  'listings.publishTitle': 'Publish products in the e-shop {name}',
  'listings.publishHint':
    'The e-shop creates the products it does not have and updates those it has. It never deletes anything.',
  'listings.byProducts': 'Chosen products',
  'listings.byCategory': 'A category with its subcategories',
  'listings.byAll': 'All active products',
  'listings.products': 'Products the e-shop does not sell yet',
  'listings.category': 'Category',
  'listings.resendTitle': 'Send all products again?',
  'listings.resendMessage':
    'At the next delivery every published product goes to the e-shop with its categories, brands and features — changed or not.',
  'listings.importAllTitle': 'Take all products from the e-shop?',
  'listings.importAllMessage':
    'The e-shop sends what it has of each product. The app fills only what it lacks — it overwrites nothing.',
  'listings.done': 'Done: {count} products.',
  'listingState.PENDING': 'Waiting to be sent',
  'listingState.SENT': 'In the e-shop',
  'listingState.FAILED': 'Failed',
  'productShops.title': 'E-shops',
  'productShops.hint': 'The e-shops the product is published in, and how it got there.',
  'productShops.empty': 'The product is in no e-shop.',
  'detail.fillFromShop': 'Fill from the e-shop',
  'detail.fillFromShopTitle': 'Fill the app from {name}?',
  'detail.fillFromShopMessage':
    'The shop sends its products at its next run. Those the app lacks are created with their quantities; nothing the app has is overwritten, and its stock does not change.',
  'detail.importPending':
    'Waiting for the shop’s products (asked {when}). The shop sends them at its next run.',
  'integrations.awaitingChoice': 'Waiting for the first sync',
  'firstSync.title': 'First sync',
  'firstSync.awaiting':
    'The shop is connected, but nothing syncs until you choose which side wins. Its catalogue is already matched with the app’s products; its orders are recorded and their stock waits for your choice.',
  'firstSync.held': 'Held orders and returns: {count}',
  'firstSync.matched': 'Matched items: {mapped} · shop items without an app product: {unmatched}',
  'firstSync.overwrites': 'What is overwritten',
  'firstSync.creates': 'What is created',
  'firstSync.heldOrders': 'Held orders',
  'firstSync.APP_TO_SHOP': 'Update the e-shop from the app',
  'firstSync.APP_TO_SHOP.overwrites':
    'The quantities of matched items in the shop are overwritten with the app’s stock. The app does not change.',
  'firstSync.APP_TO_SHOP.creates':
    'Nothing. App products the shop does not have ({count}) can be published on the Products in the e-shop tab; shop products without an app product stay unmatched.',
  'firstSync.APP_TO_SHOP.held':
    'Taken from the app’s stock right after the choice, in the order they came — before the shop gets its levels.',
  'firstSync.SHOP_TO_APP': 'Fill the app from the e-shop',
  'firstSync.SHOP_TO_APP.overwrites':
    'Nothing. Products the app already has are only filled where their EAN, code or price is missing.',
  'firstSync.SHOP_TO_APP.creates':
    'The shop’s products the app lacks — with their name, codes, price with VAT, variants, and batches by expiry. The shop’s quantities enter the stock as an “Opening stock from the e-shop” receipt wherever the app holds nothing.',
  'firstSync.SHOP_TO_APP.held':
    'Those from before the shop’s snapshot are already in its quantities — only recorded, so no sale counts twice. Later ones are taken from the stock.',
  'firstSync.unsupported':
    'Needs the shop’s module 1.2.0 or newer (now {version}) and the right to write stock changes.',
  'firstSync.choose': 'Choose',
  'firstSync.confirmTitle': '{direction}?',
  'firstSync.confirm.APP_TO_SHOP':
    'The quantities of matched items in the shop are overwritten with the app’s stock, and {count} held changes are taken. This cannot be undone.',
  'firstSync.confirm.SHOP_TO_APP':
    'The shop sends all its products and quantities at its next run. What the app lacks is created; nothing is overwritten. This cannot be undone.',
  'firstSync.importing':
    'Waiting for the shop’s products — it sends them at its next run (the cron, or “Synchronise now” in the module). Items received so far: {items}.',
  'firstSync.done': '{direction} — chosen by {who}, {when}.',
  'firstSync.result.productsCreated': 'Products created',
  'firstSync.result.variantsCreated': 'Variants created',
  'firstSync.result.batchesCreated': 'Batches created',
  'firstSync.result.filled': 'Products filled',
  'firstSync.result.receipts': 'Opening receipts',
  'firstSync.result.receiptQuantity': 'Pieces received',
  'firstSync.result.stockKept': 'App stock kept',
  'firstSync.result.skipped': 'Skipped',
  'firstSync.result.failed': 'Refused',
  'firstSync.result.heldApplied': 'Held changes applied',
  'firstSync.result.heldAbsorbed': 'Held changes already in the snapshot',
  'firstSync.unlisted': 'App products the shop does not have: {count}',
  'firstSync.unlistedHint':
    'Publish them on the Products in the e-shop tab, if the e-shop’s module takes products.',
  'firstSync.unlistedMore': '… and {count} more',
  'mappingStatus.MATCHED': 'Mapped',
  'mappingStatus.UNMATCHED': 'Unmatched',
  'mappingStatus.IGNORED': 'Ignored',
  'matchMethod.EAN': 'by EAN',
  'matchMethod.REFERENCE': 'by reference',
  'matchMethod.MANUAL': 'by hand',
  'matchMethod.CREATED': 'created for a batch',
  'matchMethod.IMPORTED': 'created from the e-shop',
  'unmatchedReason.NO_PRODUCT': 'no product with this EAN or reference',
  'unmatchedReason.NO_BATCH': 'the product has no batch of this expiry',
  'mapping.shopName': 'Shop item',
  'mapping.codes': 'EAN · reference',
  'mapping.appProduct': 'Stock product',
  'mapping.map': 'Map',
  'mapping.change': 'Change',
  'mapping.unmap': 'Unmap',
  'mapping.ignore': 'Ignore',
  'mapping.empty': 'No items',
  'mapping.emptyDetail': 'Items appear when the shop sends its catalogue — on connecting and on a full sync.',
  'mapping.title': 'Map a shop item',
  'mapping.shopItem': '{name} (#{id})',
  'mapping.product': 'Stock product',
  'mapping.batch': 'Batch',
  'mapping.batchHint':
    'The whole product: the shop sees the sum of its batches and an order is issued earliest expiry first.',
  'mapping.batchHintExpiry': 'A variant with an expiry date is a batch of the product.',
  'mapping.wholeProduct': 'The whole product (earliest expiry first)',
  'mapping.newBatch': 'A new batch expiring {date}',
  'conflicts.show': 'Show',
  'conflicts.open': 'Open',
  'conflicts.kind': 'Kind',
  'conflicts.order': 'Order',
  'conflicts.quantity': 'Pieces',
  'conflicts.detail': 'What happened',
  'conflicts.resolved': 'Resolved',
  'conflicts.resolve': 'Resolved',
  'conflicts.bySystem': 'automatically',
  'conflicts.empty': 'No conflicts',
  'conflicts.emptyDetail':
    'A conflict arises when a shop sells more than is in stock, reports an unmapped item, or cannot be reached.',
  'conflictKind.OVERSELL': 'Sold without stock',
  'conflictKind.UNMAPPED': 'Unmapped item',
  'conflictKind.REJECTED': 'Refused change',
  'conflictKind.UNREACHABLE': 'Shop unreachable',
  'log.flow': 'Direction',
  'log.IN': 'Shop → stock',
  'log.OUT': 'Stock → shop',
  'log.operation': 'Operation',
  'log.outcome': 'Outcome',
  'log.items': 'Items',
  'log.duration': 'Duration',
  'log.detail': 'Detail',
  'log.empty': 'No communication yet',
  'log.emptyDetail': 'Every request either way is written here — refused ones too.',
  'logOutcome.OK': 'ok',
  'logOutcome.PARTIAL': 'partial',
  'logOutcome.REJECTED': 'refused',
  'logOutcome.ERROR': 'error',
  'licence.integrationHint': 'How many shops may be linked. Empty = none.',
  'platformIntegrations.title': 'Linked e-shops',
  'platformIntegrations.subtitle': 'Every shop of every business, and how it stands.',
  'platformIntegrations.tenant': 'Business · company',
  'platformIntegrations.attention': 'Attention',
  'platformIntegrations.needsAttention': 'Conflicts, unmapped or unsent',
  'platformIntegrations.empty': 'No linked e-shop',
  'platformIntegrations.emptyDetail': 'A business links its shops itself, when its licence allows it.',
  'platform.PRESTASHOP': 'PrestaShop',
  'licence.metric.integrationLinks': 'Linked {platform} e-shops',
  'price.integrationLink': 'Linked {platform} e-shop (€ / month)',
  'price.integrationLinkHint': 'For each shop of this platform the licence allows.',
  'linkSettings.platform': 'Shop platform',
  'linkSettings.platformHint':
    'Only a connector of this platform can use the link; it counts against that platform in the licence.',
  'detail.versionsValue': '{platform} {version} · connector {connector}',
  'connectionToken.install.PRESTASHOP':
    'Install the “Stock sync” module (stocksync) in the PrestaShop back office.',
  'common.delete': 'Delete',
  'common.moveUp': 'Move up',
  'common.moveDown': 'Move down',
  'nav.optionGroups': 'Variant options',
  'field.variant': 'Variant',
  'optionGroups.title': 'Variant options',
  'optionGroups.subtitle': 'What the company’s products vary by in their variants — size, colour, pack.',
  'optionGroups.create': 'New group',
  'optionGroups.rename': 'Rename the group',
  'optionGroups.name': 'Group name',
  'optionGroups.values': 'Values',
  'optionGroups.editValues': 'Values',
  'optionGroups.valuesTitle': 'Values of {name}',
  'optionGroups.newValue': 'New value',
  'optionGroups.addValue': 'Add',
  'optionGroups.renameValue': 'Rename the value',
  'optionGroups.noValues': 'No values yet',
  'optionGroups.position': 'Order',
  'optionGroups.searchHint': 'group name or value',
  'optionGroups.empty': 'No option groups',
  'optionGroups.emptyManage': 'Add the first group, for example Size with S, M and L.',
  'optionGroups.emptyRead': 'The company has no option groups yet, or the filter found nothing.',
  'optionGroups.deleteTitle': 'Delete the group {name}?',
  'optionGroups.deleteMessage':
    'The group is deleted with all its values. While a product varies by it, it cannot be deleted.',
  'products.variants': 'Variants',
  'variants.title': 'Variants',
  'variants.create': 'New variant',
  'variants.edit': 'Edit the variant',
  'variants.enable': 'Sell in variants',
  'variants.axes': 'Product options',
  'variants.axesTitle': 'What the product varies by',
  'variants.axesHint':
    'Choose the option groups in the order they name the variants. Empty — a product without variants.',
  'variants.axesDefault': 'Value of “{group}” for the existing variants',
  'variants.axesDefaultHint':
    'The existing variants do not have this option yet — they get the value chosen.',
  'variants.axesNone': 'The company has no option groups. Create them under Variant options.',
  'variants.manageGroups': 'Manage options',
  'variants.values': 'Values',
  'variants.default': 'Default',
  'variants.makeDefault': 'Default variant (pre-selected in the shop)',
  'variants.price': 'Variant price ({currency})',
  'variants.priceHint': 'With VAT. Empty — the product’s price.',
  'variants.inherited': 'from the product',
  'variants.noValues': 'no values',
  'variants.needValues': 'The product has options but this variant has no values yet — give it its values.',
  'variants.valueRequired': 'Choose a value for each of the product’s options.',
  'variants.remove': 'Remove',
  'variants.removeTitle': 'Remove the variant {name}?',
  'variants.removeArchive':
    'The variant has batches, so it is archived: it keeps its stock but gets no new batches or receipts.',
  'variants.removeDelete': 'The variant has no batches — it is deleted.',
  'variants.onlyActive': 'Active only',
  'variants.empty': 'No variant',
  'variants.emptyDetail': 'The filter found nothing.',
  'batches.allVariants': 'All variants',
  'stock.variantRequired': 'Choose a variant.',
  'mapping.variant': 'Variant',
  'mapping.wholeVariable': 'The whole product (every variant)',
  'mapping.variantHint':
    'A shop item is one variant of the product; the whole product shows the sum of every variant.',
  'nav.contentLanguages': 'Content languages',
  'languages.title': 'Content languages',
  'languages.subtitle':
    'The languages the business writes the names of its products, option groups and values in.',
  'languages.ruleDefault':
    'The first language is the default: its texts are required and appear in lists and search.',
  'languages.ruleSwitch':
    'Another language becomes the default by moving it first — only when every product, option group and value has a text in it.',
  'languages.ruleRemove':
    'A removed language is hidden, but its texts stay stored and come back when it is added again.',
  'languages.default': 'Default',
  'languages.defaultTab': '{code} · default',
  'languages.defaultNotRemovable': 'The default language cannot be removed — move another language first.',
  'languages.remove': 'Remove language',
  'languages.add': 'Add a language',
  'languages.addButton': 'Add',
  'languages.readOnly':
    'Content languages are changed by the owner or whoever holds the “Content languages” permission.',
  'languages.switchWarning':
    '{name} becomes the default language. Saving works only when every text is filled in it — otherwise you are told how many are missing.',
  'languages.removeNotice': 'Removing: {names}. Their texts stay stored, hidden.',
  'languages.saved': 'The content languages are saved.',
  'languages.missing': 'Translation missing',
  'languages.missingIn': 'missing: {codes}',
  'languages.translationHint': 'Optional. An empty field deletes the translation.',
  'languages.defaultRequired': 'Fill in the text in the default language.',
  'languages.productMissing': 'The product name is missing in: {codes}. Add it by editing the product.',
  'products.section.texts': 'Texts',
  'products.section.codes': 'Codes',
  'products.section.prices': 'Prices',
  'products.section.measures': 'Dimensions and weight',
  'products.section.display': 'Display',
  'products.section.stock': 'Stock',
  'field.shortDescription': 'Short description',
  'field.description': 'Description',
  'field.slug': 'URL key (slug)',
  'products.slugHint': 'Lower-case letters without accents, digits and hyphens. Empty = made from the name.',
  'products.slugGenerate': 'Make from the name',
  'products.htmlHint':
    'HTML: p, br, strong, em, u, ul, ol, li, a, h2–h4, blockquote. Anything else is removed on saving.',
  'products.htmlEdit': 'Edit',
  'products.htmlPreview': 'Preview',
  'products.previewEmpty': 'No text yet.',
  'field.mpn': 'MPN (manufacturer code)',
  'field.grossPrice': 'Price incl. VAT ({currency})',
  'products.grossPriceHint': 'The regular price. Applies to batches without their own. Empty = no price.',
  'products.netPrice': 'Excl. VAT: {price}',
  'field.salePrice': 'Sale price incl. VAT ({currency})',
  'products.saleHint': 'Lower than the regular price. Without dates it always applies.',
  'field.saleFrom': 'Sale from',
  'field.saleTo': 'Sale until',
  'field.costPrice': 'Cost price excl. VAT ({currency})',
  'products.costHint': 'The purchase price of a new batch when the receipt names none.',
  'field.weightGrams': 'Weight (g)',
  'field.widthMm': 'Width (mm)',
  'field.heightMm': 'Height (mm)',
  'field.depthMm': 'Depth (mm)',
  'field.visibility': 'Visibility',
  'visibility.EVERYWHERE': 'Everywhere',
  'visibility.CATALOGUE': 'Catalogue only',
  'visibility.SEARCH': 'Search only',
  'visibility.HIDDEN': 'Hidden',
  'field.featured': 'Featured product',
  'products.featured': 'Featured',
  'products.featuredOnly': 'Featured only',
  'field.backorders': 'When out of stock',
  'backorders.DENY': 'Refuse the order',
  'backorders.ALLOW': 'Take the order',
  'backorders.ALLOW_NOTIFY': 'Take it and tell the customer',
  'field.lowStockThreshold': 'Low-stock threshold (pcs)',
  'field.minOrderQuantity': 'Minimum order quantity (pcs)',
  'products.wholeNumber': 'Weight, dimensions and the threshold are whole numbers, zero or more.',
  'products.minQuantityInvalid': 'The minimum order quantity is a whole number, at least 1.',
  'products.saleNotBelow': 'The sale price must be lower than the regular price.',
  'products.saleWindowReversed': 'A sale cannot end before it starts.',
  'products.saleWithoutPrice': 'Enter the sale period together with a sale price.',
  'products.price': 'Price incl. VAT',
  'products.vat': 'VAT',
  'products.onSale': 'Sale',
  'products.priceNow': 'Price now',
  'products.regularPrice': 'Regular price',
  'products.properties': 'Properties',
  'products.saleNone': 'No sale',
  'products.saleAlways': 'always on',
  'products.saleEnded': 'ended',
  'products.saleRunning': 'running',
  'products.saleComing': 'upcoming',
  'products.saleWindow': '{from} – {to}',
  'products.dimensions': '{width} × {height} × {depth} mm',
  'products.noDescription': 'No description in this language.',
  'products.hiddenHint': 'Visibility: {visibility}',
  'variants.weightHint': 'Empty = the product’s weight.',
  'common.yes': 'yes',
  'common.no': 'no',
  'variants.weightInvalid': 'The weight is a whole number, zero or more.',
  'gallery.title': 'Photos',
  'gallery.count': '{count} of {max}',
  'gallery.add': 'Add photos',
  'gallery.dropHint':
    'Drag photos here from your files. Drag a photo or use the arrows to change the order; the star marks the main photo.',
  'gallery.empty': 'This product has no photos yet',
  'gallery.emptyManage': 'Drop photos here or choose them — JPEG, PNG, WebP or HEIC, at most 15 MB each.',
  'gallery.emptyRead': 'Photos are added by someone who may edit products.',
  'gallery.tooLarge': 'The photo is too large — at most {max} MB.',
  'gallery.upload.waiting': 'Waiting',
  'gallery.upload.sending': 'Uploading',
  'gallery.upload.done': 'Uploaded',
  'gallery.upload.refused': 'Refused',
  'gallery.clearUploads': 'Hide the upload list',
  'gallery.main': 'Main photo',
  'gallery.makeMain': 'Make it the main photo',
  'gallery.altMissing': 'No description',
  'gallery.moveEarlier': 'Move earlier',
  'gallery.moveLater': 'Move later',
  'gallery.edit': 'Description and variants',
  'gallery.editTitle': 'Photo — description and variants',
  'gallery.alt': 'Photo description (alt)',
  'gallery.altHint':
    'Briefly, what the photo shows — for screen readers and search engines. At most 255 characters.',
  'gallery.variants': 'Variants in the photo',
  'gallery.variantsHint': 'With none chosen, the photo shows the product as a whole.',
  'gallery.removeTitle': 'Delete the photo?',
  'gallery.removeMessage': 'The photo leaves the product’s gallery. This cannot be undone.',
  'gallery.removeMainMessage':
    'The photo leaves the product’s gallery and the next one becomes the main photo. This cannot be undone.',
  'gallery.view': 'View photo {position}',
  'gallery.preview': 'Photo preview',
  'gallery.previous': 'Previous photo',
  'gallery.next': 'Next photo',
  'gallery.position': '{position} of {count}',
  'gallery.noPhoto': 'No photo',
  'tenants.imageStorage': 'Photos in storage: {size}',
  'nav.orders': 'Orders',
  'orders.title': 'Orders',
  'orders.subtitle':
    'The orders of the linked e-shops — they come only from the shops; the app never changes them.',
  'orders.noCompanyDetail': 'Orders belong to the company whose e-shops are linked. Create a company first.',
  'orders.shop': 'E-shop',
  'orders.reference': 'Reference',
  'orders.placedAt': 'Placed',
  'orders.customer': 'Customer',
  'orders.status': 'Status',
  'orders.total': 'Total incl. VAT',
  'orders.from': 'From',
  'orders.to': 'To',
  'orders.search': 'Reference, name or e-mail',
  'orders.empty': 'No orders',
  'orders.emptyDetail':
    'Orders appear once an e-shop whose module reports them sends them (PrestaShop stocksync 1.3.0 or newer). Try other filters too.',
  'orders.past': 'past',
  'orders.pastHint': 'An older order the e-shop sent after the first sync — shown only; it moved no stock.',
  'orderStatus.NEW': 'New',
  'orderStatus.PAID': 'Paid',
  'orderStatus.PROCESSING': 'Processing',
  'orderStatus.SHIPPED': 'Shipped',
  'orderStatus.DELIVERED': 'Delivered',
  'orderStatus.CANCELLED': 'Cancelled',
  'orderStatus.RETURNED': 'Returned',
  'orderStatus.OTHER': 'Other',
  'order.title': 'Order {reference}',
  'order.subtitle': '{shop} · {platform} · shop id {id}',
  'order.oneWay':
    'Orders only travel from the e-shop into the app. Change an order in the e-shop — its new status arrives here.',
  'order.customer': 'Customer',
  'order.billing': 'Billing address',
  'order.delivery': 'Delivery address',
  'order.noAddress': 'The e-shop did not send it.',
  'order.vatId': 'VAT id {vatId}',
  'order.payment': 'Payment',
  'order.shipping': 'Shipping',
  'order.lines': 'Lines',
  'order.item': 'Item',
  'order.quantity': 'Qty',
  'order.unitNet': 'Price excl. VAT',
  'order.unitGross': 'Price incl. VAT',
  'order.vat': 'VAT',
  'order.lineTotal': 'Total incl. VAT',
  'order.unmatched': 'Not matched to a product',
  'order.shopIds': 'in the shop {product}{variant}',
  'order.totals': 'Totals',
  'order.productsTotal': 'Goods incl. VAT',
  'order.shippingTotal': 'Shipping incl. VAT',
  'order.discountTotal': 'Discounts incl. VAT',
  'order.totalNet': 'Total excl. VAT',
  'order.totalGross': 'Total incl. VAT',
  'order.paid': 'Paid',
  'order.history': 'Status history',
  'order.stockChanges': 'Stock changes from the e-shop',
  'order.stockChangesHint':
    'Only these changes the e-shop sends for the lines move stock — never the order itself.',
  'order.noStockChanges': 'The e-shop sent no stock change for this order.',
  'changeReason.ORDER': 'Order',
  'changeReason.CANCEL': 'Cancellation',
  'changeReason.RETURN': 'Return',
  'changeOutcome.APPLIED': 'applied',
  'changeOutcome.PARTIAL': 'partly — goods were short',
  'changeOutcome.UNMAPPED': 'unmatched item',
  'changeOutcome.REJECTED': 'refused',
  'changeOutcome.HELD': 'waits for the first sync',
  'changeOutcome.ABSORBED': 'already in the shop’s stock',
  'detail.tabOrders': 'Orders',
  'detail.ordersHistory': 'Past orders',
  'detail.ordersHistoryAwaited': 'awaited from the shop',
  'detail.ordersHistoryDone': 'received {when}',
  'products.orders': 'Orders from the e-shops',
  'products.ordersHint': 'The orders this product was in.',
  'nav.categories': 'Categories',
  'nav.brands': 'Brands',
  'nav.tags': 'Tags',
  'nav.features': 'Features',
  'categories.title': 'Categories',
  'categories.subtitle': "The company's category tree — a product can be in several, one of them its main.",
  'categories.create': 'New category',
  'categories.createChild': 'Add a subcategory',
  'categories.edit': 'Edit category',
  'categories.name': 'Name',
  'categories.description': 'Description',
  'categories.slug': 'Address (slug)',
  'categories.slugHint': 'Left empty, it is made from the name.',
  'categories.parent': 'Parent category',
  'categories.top': 'Top level',
  'categories.active': 'Show in the shops',
  'categories.inactive': 'hidden',
  'categories.products': 'Products',
  'categories.productsHint': '{direct} directly · {total} with subcategories',
  'categories.expandAll': 'Expand all',
  'categories.collapseAll': 'Collapse all',
  'categories.expand': 'Expand',
  'categories.collapse': 'Collapse',
  'categories.indent': 'Nest under the category above',
  'categories.outdent': 'Move one level up',
  'categories.searchHint': 'name in any language',
  'categories.empty': 'No category',
  'categories.emptyManage': 'Add the first category, for example Dairy.',
  'categories.emptyRead': 'The company has no categories yet, or the filter found none.',
  'categories.deleteTitle': 'Delete category {name}?',
  'categories.deleteMessage': 'The category is deleted for good.',
  'categories.deleteChildren': 'The category has subcategories ({count}) — move or delete them first.',
  'categories.deleteMove': 'Move its products ({count}) to the parent category',
  'categories.deleteMoveTop': 'Take it off its products ({count}) — it is at the top level',
  'categories.depthHint': 'The tree can have at most {max} levels.',
  'categories.viewProducts': 'Show products',
  'brands.title': 'Brands',
  'brands.subtitle': "The brands and manufacturers of the company's products.",
  'brands.create': 'New brand',
  'brands.edit': 'Edit brand',
  'brands.name': 'Name',
  'brands.nameHint': 'A proper name — the same in every language.',
  'brands.description': 'Description',
  'brands.active': 'Active',
  'brands.inactive': 'inactive',
  'brands.onlyActive': 'Active only',
  'brands.products': 'Products',
  'brands.logo': 'Logo',
  'brands.logoUpload': 'Upload a logo',
  'brands.logoReplace': 'Replace the logo',
  'brands.logoRemove': 'Remove the logo',
  'brands.logoHint': 'JPEG, PNG, WebP or HEIC, at most 15 MB.',
  'brands.noLogo': 'No logo',
  'brands.searchHint': 'brand name',
  'brands.empty': 'No brand',
  'brands.emptyManage': 'Add the first brand or manufacturer.',
  'brands.emptyRead': 'The company has no brands yet, or the filter found none.',
  'brands.deleteTitle': 'Delete brand {name}?',
  'brands.deleteMessage': 'Its products ({count}) are left without a brand and the logo is deleted.',
  'tags.title': 'Tags',
  'tags.subtitle': 'Tags on products — on sale, new, organic.',
  'tags.create': 'New tag',
  'tags.edit': 'Rename tag',
  'tags.name': 'Name',
  'tags.products': 'Products',
  'tags.searchHint': 'name in any language',
  'tags.empty': 'No tag',
  'tags.emptyManage': 'Add the first tag, for example on sale.',
  'tags.emptyRead': 'The company has no tags yet, or the filter found none.',
  'tags.deleteTitle': 'Delete tag {name}?',
  'tags.deleteMessage': 'The tag comes off its products ({count}).',
  'features.title': 'Features',
  'features.subtitle':
    'What the company describes products by without variants — material, origin, allergens.',
  'features.create': 'New feature',
  'features.rename': 'Rename feature',
  'features.name': 'Feature name',
  'features.values': 'Values',
  'features.editValues': 'Values',
  'features.valuesTitle': 'Values of {name}',
  'features.newValue': 'New value',
  'features.addValue': 'Add',
  'features.renameValue': 'Rename value',
  'features.noValues': 'No predefined values — products state their own text',
  'features.position': 'Order',
  'features.products': 'Products',
  'features.valueProducts': '{count} products',
  'features.searchHint': 'feature or value',
  'features.empty': 'No feature',
  'features.emptyManage': 'Add the first feature, for example Material with cotton and polyester.',
  'features.emptyRead': 'The company has no features yet, or the filter found none.',
  'features.deleteTitle': 'Delete feature {name}?',
  'features.deleteMessage': 'The feature is deleted with its values and its products ({count}) lose it.',
  'classification.title': 'Classification',
  'classification.categories': 'Categories',
  'classification.categoriesHint': 'Mark the main category with the star.',
  'classification.main': 'Main category',
  'classification.markMain': 'Make it the main one',
  'classification.mainChip': 'main',
  'classification.brand': 'Brand',
  'classification.noBrand': 'No brand',
  'classification.tags': 'Tags',
  'classification.tagsHint': 'Type a new tag and press Enter — it is made when you save.',
  'classification.features': 'Features',
  'classification.featureNone': 'Not set',
  'classification.featureCustom': 'Own text…',
  'classification.customText': 'Own text',
  'classification.customDefaultRequired': "Fill in a feature's own text at least in the default language.",
  'classification.noCategories': 'No category',
  'classification.noTags': 'No tags',
  'classification.noFeatures': 'No features',
  'classification.noFeatureGroups': 'The company has no features yet — add them under Stock › Features.',
  'classification.ownText': 'own text',
  'products.mainCategory': 'Main category',
  'products.brand': 'Brand',
  'products.category': 'Category',
  'products.tag': 'Tag',
  'legal.title': 'Legal',
  'legal.condition': 'Condition',
  'condition.NEW': 'New',
  'condition.USED': 'Used',
  'condition.REFURBISHED': 'Refurbished',
  'legal.showCondition': 'Show the condition in the shop',
  'legal.conditionShown': 'shown in the shop',
  'legal.conditionHidden': 'not shown in the shop',
  'legal.unitPrice': 'Unit price',
  'legal.unitLabel': 'Unit',
  'legal.noUnit': 'No unit price',
  'legal.unitQuantity': 'Quantity in the pack',
  'legal.unitQuantityHint': 'How many units the product holds — e.g. 0.5 for 500 g priced per kg.',
  'legal.unitPriceValue': '{price} / {unit}',
  'legal.unitPriceNone': 'The unit price follows from the price, the unit and the quantity.',
  'legal.unitPriceHint': 'Required in the EU for goods sold by weight, volume, length or area.',
  'legal.packOf': '{quantity} {unit} in the pack',
  'legal.ecotax': 'Eco-tax with VAT ({currency})',
  'legal.ecotaxLabel': 'Eco-tax',
  'legal.ecotaxHint': 'Part of the price, not added to it — as PrestaShop holds it.',
  'legal.ecotaxNone': 'None',
  'legal.unitIncomplete': 'Give the unit price both its unit and its quantity, or leave both empty.',
  'legal.unitQuantityInvalid': 'The quantity in the pack is a positive number with at most three decimals.',
  'legal.ecotaxAbovePrice': 'The eco-tax cannot be higher than the price.',
  'legal.variantUnitQuantity': 'Quantity in the pack ({unit})',
  'legal.variantUnitQuantityHint': "Empty = the product's quantity ({quantity} {unit}).",
  'unit.kg': 'kilogram (kg)',
  'unit.g': 'gram (g)',
  'unit.l': 'litre (l)',
  'unit.ml': 'millilitre (ml)',
  'unit.m': 'metre (m)',
  'unit.m2': 'square metre (m²)',
  'unit.piece': 'piece (pc)',
  'unitSymbol.piece': 'pc',
  'availability.title': 'Availability',
  'availability.texts': 'Availability texts',
  'availability.availableNow': 'Text while in stock',
  'availability.availableNowHint': 'E.g. “In stock”.',
  'availability.availableLater': 'Text while out of stock',
  'availability.availableLaterHint': 'E.g. “Ships in 3 days” — for a back-order or a pre-order.',
  'availability.availableFrom': 'Available from',
  'availability.availableFromHint': 'For a pre-order: from when the goods ship.',
  'availability.flags': 'Ordering',
  'availability.availableForOrder': 'Available for order',
  'availability.showPrice': 'Show the price',
  'availability.showPriceHint': 'A product whose price is hidden cannot be ordered.',
  'availability.onlineOnly': 'Online only (not in the store)',
  'availability.onSaleBadge': '“On sale” badge in the shop',
  'availability.onSaleBadgeHint': 'Independent of the sale price.',
  'availability.noText': 'No text in this language.',
  'availability.noDate': 'No date',
  'availability.priceHiddenOrderable':
    'A product whose price is hidden cannot be ordered — switch off “Available for order” too.',
  'seo.title': 'SEO',
  'seo.metaTitle': 'Search-engine title',
  'seo.metaDescription': 'Search-engine description',
  'seo.count': '{count} / {max} characters',
  'seo.countLong': '{count} / {max} characters — search engines show about {advised}',
  'seo.titleFallback': "When empty, the shops use the product's name.",
  'seo.descriptionFallback': "When empty, the shops use the product's short description.",
  'seo.usesName': 'Empty — the shops use the name: {name}',
  'seo.usesShortDescription': 'Empty — the shops use the short description.',
  'seo.tooLong': 'The SEO title has at most 70 characters and the SEO description at most 160.',
  'relations.title': 'Related products',
  'relations.hint':
    'Which products the shop offers with this one. PrestaShop shows accessories; WooCommerce up-sells and cross-sells.',
  'relations.kind.ACCESSORY': 'Accessories',
  'relations.kind.UP_SELL': 'Up-sells',
  'relations.kind.CROSS_SELL': 'Cross-sells',
  'relations.kindHint.ACCESSORY': 'Shown on the product page.',
  'relations.kindHint.UP_SELL': 'Offered instead of this product, on its page.',
  'relations.kindHint.CROSS_SELL': 'Offered beside this product, in the cart.',
  'relations.empty': 'None yet.',
  'relations.add': 'Add a product',
  'relations.searchHint': 'name, SKU or EAN',
  'relations.reciprocal': 'Add both ways',
  'relations.moveUp': 'Move up',
  'relations.moveDown': 'Move down',
  'relations.remove': 'Remove',
  'relations.archived': 'archived',
  'field.productType': 'Product type',
  'productType.STANDARD': 'Standard product',
  'productType.PACK': 'Pack',
  'productType.hint.STANDARD': 'Goods stocked in batches.',
  'productType.hint.PACK':
    'Made of other products’ variants. It holds no stock or batches of its own — it sells as many packs as its scarcest item allows.',
  'products.section.type': 'Type',
  'products.packsAvailable': 'Packs available',
  'products.packItemCount': 'Pack items',
  'products.packHint': 'from its items',
  'pack.title': 'Pack contents',
  'pack.hint':
    'A pack holds no stock of its own: it sells as many packs as its scarcest item allows. Selling one issues its items from their batches (FEFO) in one operation; a return puts them back.',
  'pack.available': 'Available: {count} packs',
  'pack.limitedBy': 'Limited by: {name}',
  'pack.limiting': 'limiting',
  'pack.empty': 'The pack has no items yet — add the products it is made of.',
  'pack.add': 'Add a product',
  'pack.searchHint': 'name, SKU or EAN',
  'pack.variant': 'Variant',
  'pack.quantity': 'Pcs per pack',
  'pack.addButton': 'Add',
  'pack.onHand': '{count} pcs on hand',
  'pack.covers': 'enough for {count} packs',
  'pack.perPack': '× {count}',
  'pack.archived': 'archived',
  'pack.moveUp': 'Move up',
  'pack.moveDown': 'Move down',
  'pack.remove': 'Remove from the pack',
  'pack.quantityInvalid': 'The quantity must be a whole number from 1 to 9,999.',
  'pack.variantRequired': 'Choose a variant.',
  'stock.packIssueHint':
    '{count} packs will be issued — each item from its own batches, FEFO, all in one operation; if any item is short, nothing is issued:',
  'stock.packIssueLine': '{name}: {count} pcs',
  'movements.inPack': 'in pack {name}',
  'productType.VIRTUAL': 'Virtual product',
  'productType.hint.VIRTUAL': 'Nothing is shipped — an e-book, a voucher, a licence.',
  'files.downloadsTitle': 'Downloads',
  'files.downloadsHint':
    'What a customer gets with the virtual product — private, opened only by a link valid for 5 minutes. PDF, ZIP, images, text, Office, EPUB, MP3, MP4; at most {maxMb} MB.',
  'files.attachmentsTitle': 'Attachments',
  'files.attachmentsHint':
    'Documents on the product page — manuals, safety sheets. PDF, ZIP, images, text, Office; at most {maxMb} MB.',
  'files.downloadsEmpty': 'No downloads yet — upload what the customer gets.',
  'files.attachmentsEmpty': 'No attachments yet.',
  'files.upload': 'Upload a file',
  'files.uploading': 'Uploading… {percent} %',
  'files.open': 'Open',
  'files.edit': 'Edit',
  'files.editTitle': 'Edit file',
  'files.remove': 'Remove',
  'files.removeTitle': 'Remove the file?',
  'files.removeMessage': '“{name}” will be removed from the product and from storage.',
  'files.moveUp': 'Move up',
  'files.moveDown': 'Move down',
  'files.name': 'Name',
  'files.description': 'Description',
  'files.maxDownloads': 'Downloads allowed',
  'files.validityDays': 'Valid for (days)',
  'files.unlimitedHint': 'Empty — no limit.',
  'files.downloadsCount': '{count} downloads',
  'files.daysCount': '{count} days',
  'files.limitInvalid': 'Downloads are a whole number from 1 to 1,000,000, validity from 1 to 3,650 days.',
  'files.typeRefused': 'This kind of file cannot be uploaded here.',
  'files.tooLarge': 'The file is empty or larger than {maxMb} MB.',
  'products.alwaysAvailable': 'Always available',
  'products.virtualMeasures':
    'A virtual product is not shipped — its weight and dimensions are cleared when saved.',
  'customization.title': 'Customization',
  'customization.hint':
    'What a customer fills in when ordering in the shop — a text or a file (a photo). The values they enter arrive with orders later.',
  'customization.empty': 'The product is not customized.',
  'customization.add': 'Add a field',
  'customization.edit': 'Edit the field',
  'customization.remove': 'Remove the field',
  'customization.moveUp': 'Move up',
  'customization.moveDown': 'Move down',
  'customization.label': 'Field label',
  'customization.type': 'Kind',
  'customization.type.TEXT': 'Text',
  'customization.type.FILE': 'File',
  'customization.required': 'Required — the order cannot be sent without it',
  'customization.requiredChip': 'required',
  'customization.labelRequired': 'Fill in the label in the default language.',
  'nav.transfers': 'Data transfer',
  'movementReason.TRANSFER_IMPORT': 'Imported from a file',
  'transfers.title': 'Data transfer',
  'transfers.subtitle':
    'The company’s catalogue and stock to a file and from one — into another company, another business, or from an e-shop without a link.',
  'transfers.noCompanyDetail': 'A transfer belongs to a company. Create a company first.',
  'transfers.history': 'Transfer history',
  'transfers.exportTitle': 'Export',
  'transfers.exportHint':
    'Products with their variants, texts in every language, prices, categories, brands, features and stock per batch. The file is made in the background and can be downloaded for 7 days; we also send you an e-mail.',
  'transfers.scopeAll': 'The whole catalogue',
  'transfers.scopeCategory': 'One category with its subcategories',
  'transfers.category': 'Category',
  'transfers.includeMedia': 'Photos and product files too',
  'transfers.exportStart': 'Export',
  'transfers.importTitle': 'Import',
  'transfers.importHint':
    'A ZIP file from an export (at most {maxMb} MB) is uploaded straight to storage and checked. The preview shows what would be created, filled or skipped, and only then do you confirm the import.',
  'transfers.chooseFile': 'Choose a file',
  'transfers.noFile': 'No file chosen',
  'transfers.uploading': 'Uploading… {percent} %',
  'transfers.importStart': 'Upload and check',
  'transfers.notZip': 'Only a ZIP file made by an export can be imported.',
  'transfers.tooLarge': 'The file is empty or larger than {maxMb} MB.',
  'transfers.moreProblems': 'and {count} more problems',
  'transfers.itemSearch': 'Name, SKU or EAN',
  'transfers.match': 'Match',
  'transfers.outcome': 'Outcome',
  'transfers.product': 'Product',
  'transfers.wouldDo': 'What happens',
  'transfers.itemShape': '{variants} variants · {photos} photos',
  'transfers.matchedBy': 'Matched by {by}',
  'transfers.wouldSkip': 'Skipped',
  'transfers.wouldCreate': 'Created',
  'transfers.noItems': 'No products',
  'transfers.direction': 'Direction',
  'transfers.status': 'Status',
  'transfers.createdAt': 'Started',
  'transfers.what': 'What',
  'transfers.size': 'Size',
  'transfers.finishedAt': 'Finished',
  'transfers.withMedia': 'with photos and files',
  'transfers.withoutMedia': 'without photos and files',
  'transfers.noHistory': 'No transfer yet',
  'transfers.noHistoryDetail': 'Export the company’s catalogue, or import a file from another export.',
  'transfers.download': 'Download the file',
  'transfers.availableUntil': 'Available until {date}',
  'transfers.previewTitle': 'Import preview',
  'transfers.previewFrom': 'A file from {producer}, made on {date}; languages {languages}.',
  'transfers.previewNew': 'New: {count}',
  'transfers.previewMatched': 'Already in the company: {count}',
  'transfers.previewInvalid': 'Refused: {count}',
  'transfers.previewVariants': 'Variants: {count}',
  'transfers.previewStock': 'Batches: {batches}, pieces: {quantity}',
  'transfers.previewMedia': 'Photos and files: {count}',
  'transfers.previewTaxonomy': '{kind}: {total} ({new} new)',
  'transfers.modeLabel': 'Products the company already has',
  'transfers.stockLabel': 'Stock from the file',
  'transfers.apply': 'Confirm the import',
  'transfers.importDone': 'The import is done ({mode}, {stock}).',
  'transferDirection.EXPORT': 'Export',
  'transferDirection.IMPORT': 'Import',
  'transferStatus.AWAITING_UPLOAD': 'Waiting for the file',
  'transferStatus.QUEUED': 'Queued',
  'transferStatus.RUNNING': 'Running',
  'transferStatus.VALIDATING': 'Being checked',
  'transferStatus.READY': 'Waiting for confirmation',
  'transferStatus.APPLYING': 'Importing',
  'transferStatus.DONE': 'Done',
  'transferStatus.FAILED': 'Failed',
  'transferStatus.EXPIRED': 'File expired',
  'transferStatusHint.AWAITING_UPLOAD': 'The file is being uploaded to storage.',
  'transferStatusHint.QUEUED': 'Waiting for the background processing.',
  'transferStatusHint.RUNNING': 'The file is being made.',
  'transferStatusHint.VALIDATING': 'The file is being checked — its contents, hashes and structure.',
  'transferStatusHint.APPLYING': 'Products and stock are being imported.',
  'transferPhase.TAXONOMY': 'Categories and features',
  'transferPhase.PRODUCTS': 'Products',
  'transferPhase.LINKS': 'Related products and packs',
  'transferPhase.STOCK': 'Stock',
  'transferPhase.FINISH': 'Finishing',
  'transferMode.CREATE_ONLY': 'Create new only',
  'transferMode.FILL_EMPTY': 'Fill empty fields',
  'transferMode.OVERWRITE': 'Overwrite from the file',
  'transferMode.hint.CREATE_ONLY': 'Products the company has stay as they are.',
  'transferMode.hint.FILL_EMPTY': 'Only what a product lacks is filled; nothing is overwritten.',
  'transferMode.hint.OVERWRITE':
    'The product becomes what the file says — texts, prices, variants, photos, files.',
  'transferMode.would.CREATE_ONLY': 'Skipped',
  'transferMode.would.FILL_EMPTY': 'Filled',
  'transferMode.would.OVERWRITE': 'Overwritten',
  'transferStock.SET_LEVELS': 'Set the levels',
  'transferStock.ADD_RECEIPT': 'Add as a receipt',
  'transferStock.IGNORE': 'Ignore',
  'transferStock.hint.SET_LEVELS': 'Each batch ends at the file’s quantity — by an inventory adjustment.',
  'transferStock.hint.ADD_RECEIPT': 'The file’s quantities are added by a receipt.',
  'transferStock.hint.IGNORE': 'Stock stays as it is.',
  'transferMatch.NEW': 'New',
  'transferMatch.MATCHED': 'In the company',
  'transferMatch.INVALID': 'Refused',
  'transferMatchedBy.SKU': 'SKU',
  'transferMatchedBy.EAN': 'EAN',
  'transferMatchedBy.NAME': 'name',
  'transferOutcome.CREATED': 'Created',
  'transferOutcome.UPDATED': 'Updated',
  'transferOutcome.SKIPPED': 'Skipped',
  'transferOutcome.FAILED': 'Failed',
  'transferCount.products': 'Products',
  'transferCount.variants': 'Variants',
  'transferCount.batches': 'Batches',
  'transferCount.taxonomy': 'Categories and features',
  'transferCount.media': 'Photos and files',
  'transferCount.created': 'Created',
  'transferCount.updated': 'Updated',
  'transferCount.skipped': 'Skipped',
  'transferCount.failed': 'Failed',
  'transferCount.taxonomyCreated': 'New categories and features',
  'transferCount.variantsCreated': 'New variants',
  'transferCount.batchesCreated': 'New batches',
  'transferCount.photos': 'Photos stored',
  'transferCount.files': 'Files stored',
  'transferCount.movements': 'Stock movements',
  'transferCount.quantity': 'Pieces moved',
  'taxonomyKind.category': 'Categories',
  'taxonomyKind.brand': 'Brands',
  'taxonomyKind.tag': 'Tags',
  'taxonomyKind.option-group': 'Variant options',
  'taxonomyKind.feature-group': 'Features',
  'transferProblem.UPLOAD_MISSING': 'The file is not in storage. Please upload it again.',
  'transferProblem.UPLOAD_TOO_LARGE': 'The file is larger than {maxMb} MB.',
  'transferProblem.UPLOAD_CHANGED':
    'The file changed since it was checked. Please upload and check it again.',
  'transferProblem.PACKAGE_UNREADABLE': 'The file is not a readable ZIP.',
  'transferProblem.MANIFEST_MISSING': 'The file has no manifest.json — it is not a file from an export.',
  'transferProblem.MANIFEST_INVALID': 'The file’s manifest is damaged.',
  'transferProblem.FORMAT_NEWER':
    'The file is in a newer format ({format}) than the application reads ({supported}). Update the application or export the file again.',
  'transferProblem.FORMAT_UNKNOWN': 'The file is not in a known format ({format}).',
  'transferProblem.FILE_MISSING': '{path} is missing from the file.',
  'transferProblem.FILE_UNLISTED': 'The file holds {path}, which is not part of the export.',
  'transferProblem.FILE_TOO_LARGE': '{path} is larger than {maxMb} MB.',
  'transferProblem.HASH_MISMATCH':
    '{path} was changed after the export — its contents do not match its hash.',
  'transferProblem.SCHEMA': 'A part of the file does not have the right structure.',
  'transferProblem.DUPLICATE_REF': 'Record {ref} is in the file twice.',
  'transferProblem.MEDIA_MISSING': 'The photo or file {path} is missing from the file.',
  'transferProblem.NO_MEDIA': 'The file holds no photos or product files — they are imported without them.',
  'transferProblem.LANGUAGE_MISSING':
    'Texts in {languages} are not imported — the business does not write in them.',
  'transferProblem.CURRENCY_MISMATCH': 'The file’s prices are in {package}; the company uses {company}.',
  'transferProblem.LICENCE_PRODUCTS':
    'The import would create {needed} products; the licence has room for {room}.',
  'transferProblem.LICENCE_STOCK':
    'The file’s stock ({needed} pcs) is over the licence’s room ({room} pcs); some will not be imported.',
  'transferProblem.LICENCE_RESTRICTED': 'The licence does not allow changes — the import stopped.',
  'transferProblem.TAXONOMY': 'Taxonomy entry {ref} was not imported.',
  'transferProblem.STOCK': 'The stock of {ref} was not imported.',
  'transferProblem.LINKS': 'The related products or pack of product {ref} were not imported in full.',
  'transferProblem.FAILED': 'The transfer failed.',
  'transferReason.MATCHED': 'The company has it already (create new only).',
  'transferReason.NOTHING_EMPTY': 'Nothing was empty.',
  'transferReason.ARCHIVED': 'The product is archived.',
  'transferReason.INVALID': 'The file refused it.',
  'transferReason.SCHEMA': 'The record in the file is not valid.',
  'transferReason.REFUSED': 'The application refused the change.',
  'nav.contacts': 'Contacts',
  'contacts.title': 'Contacts',
  'contacts.subtitle':
    'The company’s customers, partners and suppliers — their e-mails, phones, addresses and notes.',
  'contacts.noCompanyDetail': 'Contacts belong to a company. Create a company or ask for access to contacts.',
  'contacts.new': 'New contact',
  'contacts.labels': 'Labels',
  'contacts.import': 'Import',
  'contacts.export': 'Export',
  'contacts.search': 'Search — name, e-mail, phone, IČO, city, person',
  'contacts.kind': 'Kind',
  'contacts.type': 'Type',
  'contacts.label': 'Label',
  'contacts.country': 'Country',
  'contacts.doNotContact': 'Do not contact',
  'contacts.dnc.only': 'Only do not contact',
  'contacts.dnc.exclude': 'Except do not contact',
  'contacts.name': 'Name',
  'contacts.email': 'E-mail',
  'contacts.phone': 'Phone',
  'contacts.city': 'City',
  'contacts.ico': 'IČO',
  'contacts.updatedAt': 'Changed',
  'contacts.empty': 'No contacts',
  'contacts.emptyDetail': 'Add a contact or import a ZIP file of contacts.',
  'contacts.emptyFiltered': 'No contact matches the filter.',
  'contactKind.ORGANISATION': 'Organisation',
  'contactKind.PERSON': 'Person',
  'contactType.CUSTOMER': 'Customer',
  'contactType.SUPPLIER': 'Supplier',
  'contactType.PARTNER': 'Partner',
  'contactType.OTHER': 'Other',
  'contact.createTitle': 'New contact',
  'contact.delete': 'Delete',
  'contact.deleteTitle': 'Delete the contact',
  'contact.deleteMessage': 'The contact {name} is deleted with its notes and history. This cannot be undone.',
  'contact.created': 'Created by {email}, {date}',
  'contact.updated': 'Last changed by {email}, {date}',
  'contact.someone': 'unknown',
  'contact.section.identity': 'Details',
  'contact.section.emails': 'E-mails',
  'contact.section.phones': 'Phones',
  'contact.section.addresses': 'Addresses',
  'contact.section.persons': 'People',
  'contact.section.websites': 'Web and social',
  'contact.section.description': 'Description',
  'contact.section.labels': 'Labels',
  'contact.section.notes': 'Notes',
  'contact.section.history': 'History',
  'contact.name': 'Name',
  'contact.legalName': 'Registered name',
  'contact.ico': 'IČO',
  'contact.dic': 'DIČ',
  'contact.icDph': 'IČ DPH',
  'contact.doNotContactHint': 'Send no offers or other messages.',
  'contact.primary': 'Primary',
  'contact.makePrimary': 'Make primary',
  'contact.billing': 'Billing',
  'contact.operating': 'Operating',
  'contact.makeBilling': 'Make billing',
  'contact.addEmail': 'Add an e-mail',
  'contact.addPhone': 'Add a phone',
  'contact.addAddress': 'Add an address',
  'contact.addPerson': 'Add a person',
  'contact.addWebsite': 'Add a link',
  'contact.remove': 'Remove',
  'contact.rowLabel': 'Label',
  'contact.rowLabelHint': 'e.g. orders, invoicing',
  'contact.note': 'Note',
  'contact.street': 'Street and number',
  'contact.city': 'City',
  'contact.postalCode': 'Postal code',
  'contact.countryCode': 'Country',
  'contact.countryHint': 'SK, CZ, AT…',
  'contact.personName': 'Name',
  'contact.role': 'Role',
  'contact.url': 'Address',
  'contact.linkKind': 'Kind',
  'contact.phoneHint': 'Without a country code it is taken as Slovak.',
  'contact.none': 'None',
  'contact.noLabels': 'The company has no labels yet — add them with Labels on the contacts list.',
  'websiteKind.WEB': 'Web',
  'websiteKind.FACEBOOK': 'Facebook',
  'websiteKind.INSTAGRAM': 'Instagram',
  'websiteKind.LINKEDIN': 'LinkedIn',
  'websiteKind.OTHER': 'Other',
  'contactNotes.add': 'Add a note',
  'contactNotes.placeholder': 'Write a note — what was agreed, when to call back…',
  'contactNotes.empty': 'No notes yet.',
  'contactNotes.edited': 'edited {date}',
  'contactNotes.deleteTitle': 'Delete the note',
  'contactNotes.deleteMessage': 'The note is deleted. The history keeps its beginning.',
  'contactHistory.empty': 'No changes yet.',
  'contactHistory.more': 'Load older',
  'contactHistory.action.CREATED': 'Created',
  'contactHistory.action.UPDATED': 'Edited',
  'contactHistory.action.IMPORTED': 'Imported',
  'contactHistory.action.NOTE_ADDED': 'Note added',
  'contactHistory.action.NOTE_EDITED': 'Note edited',
  'contactHistory.action.NOTE_DELETED': 'Note deleted',
  'contactHistory.field.kind': 'Kind',
  'contactHistory.field.type': 'Type',
  'contactHistory.field.name': 'Name',
  'contactHistory.field.legalName': 'Registered name',
  'contactHistory.field.ico': 'IČO',
  'contactHistory.field.dic': 'DIČ',
  'contactHistory.field.icDph': 'IČ DPH',
  'contactHistory.field.doNotContact': 'Do not contact',
  'contactHistory.field.description': 'Description',
  'contactHistory.field.emails': 'E-mails',
  'contactHistory.field.phones': 'Phones',
  'contactHistory.field.addresses': 'Addresses',
  'contactHistory.field.persons': 'People',
  'contactHistory.field.websites': 'Links',
  'contactHistory.field.labels': 'Labels',
  'contactHistory.field.note': 'Note',
  'contactHistory.yes': 'yes',
  'contactHistory.no': 'no',
  'contactLabels.title': 'Contact labels',
  'contactLabels.new': 'New label',
  'contactLabels.name': 'Name',
  'contactLabels.count': '{count} contacts',
  'contactLabels.empty': 'The company has no labels yet. For example restaurant, hotel, wholesale.',
  'contactLabels.add': 'Add',
  'contactLabels.rename': 'Rename',
  'contactLabels.deleteTitle': 'Delete the label',
  'contactLabels.deleteMessage': 'The label {name} is deleted and comes off every contact.',
  'contactImport.title': 'Import contacts',
  'contactImport.intro':
    'Choose a ZIP file exported from the app. You first see what the import will do — nothing is written until you confirm.',
  'contactImport.choose': 'Choose a ZIP file',
  'contactImport.check': 'Check',
  'contactImport.uploading': 'Uploading… {percent} %',
  'contactImport.summary':
    '{total} rows: {creates} new, {updates} fill in an existing contact, {errors} with an error.',
  'contactImport.labelsToCreate': 'Labels to be created: {labels}',
  'contactImport.rules':
    'An existing contact is found by IČO, then by the primary e-mail, and a row with neither by its name. The import fills it in — it deletes nothing. A row with an error is skipped.',
  'contactImport.confirm': 'Import {count}',
  'contactImport.done':
    'Done: {created} created, {updated} filled in, {refused} skipped, {labels} new labels.',
  'contactImport.outcome': 'Outcome',
  'contactImport.position': 'Row',
  'contactImport.detail': 'Details',
  'contactImport.searchRows': 'Search the rows',
  'contactImport.outcome.CREATE': 'New',
  'contactImport.outcome.UPDATE': 'Fills in',
  'contactImport.outcome.ERROR': 'Error',
  'contactImport.matchedBy.ICO': 'by IČO',
  'contactImport.matchedBy.EMAIL': 'by e-mail',
  'contactImport.matchedBy.NAME': 'by name',
  'contactImport.aboveRow': 'a contact from a row above',
  'contactImport.problem.NOT_AN_OBJECT': 'The row is not a contact.',
  'contactImport.problem.NAME_REQUIRED': 'The name is missing.',
  'contactImport.problem.KIND_INVALID': 'Unknown kind “{value}”.',
  'contactImport.problem.TYPE_INVALID': 'Unknown type “{value}”.',
  'contactImport.problem.EMAIL_INVALID': '“{value}” is not an e-mail.',
  'contactImport.problem.PHONE_INVALID': '“{value}” is not a phone number.',
  'contactImport.problem.COUNTRY_INVALID': '“{value}” is not a country code.',
  'contactImport.problem.LIST_TOO_LONG': 'Too many items in a list.',
  'contactImport.problem.VALUE_INVALID': 'Invalid value “{value}”.',
};
